/**
 * DocsToCaptureGrid — PDW Step 4 canonical (SPEC Screen 12).
 *
 * 4-column grid of doc-capture tiles. Replaces the legacy tab-strip
 * pattern (IndividualNraDocumentScanTabs etc.) with the canonical
 * "Documents to capture" grid view. Tile state machine matches the
 * SPEC: Idle → InProgress (Step 5) → Captured (Step 8) | Failed.
 *
 * Driven by a `tiles` prop derived from WizardDocMatrix for the
 * applicant's citizenship cohort (us-citizen / green-card / nra-work
 * / nra-student / nra-exchange / nra-visitor) or business / trust /
 * fiduciary / gov / charitable / special-purpose cohort key.
 *
 * Tile click delegates to the parent — `onTileClick(tileId)` — so the
 * grid stays presentational and the parent owns the scan-modal flow
 * (Steps 5+6+7 — `pdw-5-scan-analyzing` → `pdw-6-scan-results` →
 * `pdw-7-scan-saving`). The actual capture pipeline (DocumentCaptureModule
 * + captureRouting + IdExtractionService) is unchanged.
 *
 * Continue gate (SPEC line 985): button disabled until
 * `requiredCaptured >= requiredTotal`; label dynamically reads
 * "Capture N more" or "Continue to review".
 *
 * Bill 2026-05-13 — first PDW canonical-lockdown batch (Step 4).
 */
import { Fragment, forwardRef, useEffect, useRef } from 'react';
import { Button, Pill } from '@/components/primitives';
import { documentIconUrl } from '@/icons/flat-library';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';

export type TileStatus = 'idle' | 'in-progress' | 'captured' | 'failed';
export type TileGatingLevel = 'HARD_STOP' | 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL';

/**
 * Single extracted-field row rendered inside the quick-preview drawer.
 * Mirrors the Screen 14 `Extracted fields` list shape.
 */
export interface ExtractedFieldRow {
  key: string;
  value: string;
  /** 0..1 Azure DI confidence. Omitted when not applicable. */
  confidence?: number;
}

export interface DocTileSpec {
  /** Stable tile id — matches WizardDocMatrix.TileId. */
  tileId: string;
  /** Display label e.g. "Foreign Passport". */
  label: string;
  /** One-line hint shown on the tile body. */
  hint: string;
  /** Document-icon slug from flat-library DOCUMENT_ICONS map. */
  iconSlug: string;
  /** Required vs optional vs hard-stop per WizardDocMatrix. */
  gatingLevel: TileGatingLevel;
  /** Per-tile current state. */
  status: TileStatus;
  /**
   * Step 2 (Bill 2026-05-28) — when a GENERIC tile (Primary Photo ID /
   * Secondary Photo ID) captures a doc, the tile ADOPTS the classified
   * doc's branding (label + icon + F254 code) here. Renderer reads
   * `capturedAs.label / iconSlug` in preference to the tile's static
   * label / iconSlug when present. Drives the CIP slot-machine via
   * `capturedAs.idCodeF254`.
   */
  capturedAs?: {
    /** Classified doc-type value (a DOC_TYPE_OPTIONS.value). */
    docTypeValue: string;
    /** Doc-type label adopted onto the tile (e.g. "Driver's License"). */
    label: string;
    /** Doc-type icon slug adopted onto the tile. */
    iconSlug: string;
    /** Core Director F254 IdVerifyCode for slot-machine priority. */
    idCodeF254?: string;
  };
  /** When captured: number of Azure DI fields auto-filled (for the hint). */
  fieldsRead?: number;
  /** When captured: filename of the uploaded doc (shown on the captured pill). */
  filename?: string;
  /** When captured: page count if multi-page (shown as "1/N" badge). */
  pageCount?: number;
  /** When failed: short reason. */
  failureReason?: string;
  /**
   * BACKLOG-0149 — Quick-preview drawer data (SPEC Screen 16 line 1440-1451).
   * Optional; when omitted the drawer renders the document icon as a
   * placeholder thumbnail and an "Extracted fields not available" hint.
   */
  thumbnailDataUrl?: string;
  /**
   * MIME type of the captured doc. Drives `<img>` vs PdfCanvasViewer rendering
   * in the drawer (image/* → img, application/pdf → iframe).
   */
  thumbnailMimeType?: string;
  /** Extracted fields list shown in the drawer's preview list. */
  extractedFields?: ReadonlyArray<ExtractedFieldRow>;
}

export interface DocsToCaptureGridProps {
  tiles: ReadonlyArray<DocTileSpec>;
  /** Click → parent opens the per-doc scan flow (Steps 5+6+7). */
  onTileClick: (tileId: string) => void;
  /** Drag-drop file → parent uploads + routes to scan endpoint. */
  onTileDrop?: (tileId: string, file: File) => void;
  // B1 (Bill 2026-06-03): onStackDrop (bulk "Drop a stack" multi-file ingest)
  // REMOVED — deterministic path-driven tiles only.
  /**
   * Continue/Return footer — OPTIONAL. When the grid is embedded inside an
   * outer wizard that owns its own footer (e.g. the legacy
   * IndividualNraDocumentScanTabs section inside IndividualPersonDialog),
   * omit both props and the grid renders inline without a footer. The PDW
   * canonical Step 4 modal SHOULD provide them.
   */
  onContinue?: () => void;
  onReturn?: () => void;

  // ─────────────────────────────────────────────────────────────────────────
  // BACKLOG-0149 — Quick-preview drawer (SPEC Screen 16). Controlled by the
  // parent: when `previewTileId` matches a captured tile, the grid renders
  // an inline drawer panel directly below that tile (full-width across the
  // grid). When omitted, captured-tile clicks behave like idle-tile clicks
  // (fall through to `onTileClick`).
  // ─────────────────────────────────────────────────────────────────────────
  /** Currently-previewed tile id, or null when no drawer is open. */
  previewTileId?: string | null;
  /** Esc / outside-click / Close-button → fires this. */
  onPreviewClose?: () => void;
  /** Re-scan action button — placeholder OK; parent decides routing. */
  onPreviewRescan?: (tileId: string) => void;
  /** Review import — reopen the capture's review (only for tiles in reviewableTileIds). */
  onPreviewReview?: (tileId: string) => void;
  reviewableTileIds?: ReadonlySet<string>;
  /** Add-page action button — placeholder OK; parent decides routing. */
  onPreviewAddPage?: (tileId: string) => void;
  /** Delete action button — parent confirms + soft-removes capture. */
  onPreviewDelete?: (tileId: string) => void;
}

function isRequired(level: TileGatingLevel): boolean {
  return level === 'HARD_STOP' || level === 'REQUIRED';
}

function tileStatusHint(t: DocTileSpec): string {
  switch (t.status) {
    case 'in-progress': return 'Analyzing…';
    case 'captured':    return t.fieldsRead != null
      ? `Captured · ${t.fieldsRead} field${t.fieldsRead === 1 ? '' : 's'} read`
      : 'Captured';
    case 'failed':      return `Scan failed — ${t.failureReason ?? 'click to retry'}`;
    case 'idle':
    default:            return 'Click or drop a file';
  }
}

export function DocsToCaptureGrid({
  tiles,
  onTileClick,
  onTileDrop,
  onContinue,
  onReturn,
  previewTileId,
  onPreviewClose,
  onPreviewRescan,
  onPreviewReview,
  reviewableTileIds,
  onPreviewAddPage,
  onPreviewDelete,
}: DocsToCaptureGridProps): JSX.Element {
  const requiredTiles = tiles.filter((t) => isRequired(t.gatingLevel));
  const requiredTotal = requiredTiles.length;
  const requiredCaptured = requiredTiles.filter((t) => t.status === 'captured').length;
  const totalCaptured = tiles.filter((t) => t.status === 'captured').length;

  const continueDisabled = requiredCaptured < requiredTotal;
  const continueLabel = continueDisabled
    ? `Capture ${requiredTotal - requiredCaptured} more`
    : 'Continue to review';
  const continueTooltip = continueDisabled
    ? 'Capture all required documents to enable Continue'
    : 'Advance to Review';

  const previewTile = previewTileId ? tiles.find((t) => t.tileId === previewTileId) : undefined;
  const drawerRef = useRef<HTMLDivElement | null>(null);

  // BACKLOG-0149 — Esc + outside-click close the drawer (acceptance #4).
  // Outside-click is detected by walking the event target up to the drawer
  // element + the captured tile button (so clicking the tile itself doesn't
  // immediately re-close the drawer it just opened).
  useEffect(() => {
    if (!previewTileId || !onPreviewClose) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onPreviewClose();
      }
    };
    const onClick = (e: MouseEvent): void => {
      const drawer = drawerRef.current;
      const target = e.target as Node | null;
      if (!drawer || !target) return;
      if (drawer.contains(target)) return;
      // Don't close if the click was on the source tile (that's a toggle —
      // parent decides whether to re-fire onTileClick).
      const sourceTile = drawer.parentElement?.querySelector(
        `[data-tile-id="${previewTileId}"]`,
      );
      if (sourceTile && sourceTile.contains(target)) return;
      onPreviewClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [previewTileId, onPreviewClose]);

  return (
    <div className="pdw-docs-grid">
      <header className="pdw-docs-grid__header">
        <div>
          <h3 className="pdw-docs-grid__title">Documents to capture</h3>
          <p className="pdw-docs-grid__sub">
            Click a tile to scan, or drag &amp; drop a file directly onto it.
            Required documents must be captured before continuing.
          </p>
        </div>
        <div className="pdw-docs-grid__counter">
          <strong>{requiredCaptured} / {requiredTotal}</strong> required
          <span className="pdw-docs-grid__counter-dot">·</span>
          <strong>{totalCaptured}</strong> total
        </div>
      </header>

      {/* B1 (Bill 2026-06-03): the "Drop a stack of documents" bulk multi-file
          drop zone is REMOVED. Deterministic path-driven tiles only — each
          document is captured on its own tile (no bulk auto-classify dump).
          See feedback_deterministic_tiles_kill_bulk_dump_20260531. */}

      <div className="pdw-docs-grid__tiles" role="list">
        {tiles.map((t) => {
          const required = isRequired(t.gatingLevel);
          // Step 2 — generic slot tiles (Primary Photo ID / Secondary Photo ID)
          // adopt the classified doc's branding once captured. capturedAs.label
          // / iconSlug override the tile's static label / iconSlug. Static
          // tiles (green-card / passport / visa / etc.) have no capturedAs;
          // their label + icon stay as configured.
          const displayLabel = t.capturedAs?.label ?? t.label;
          const displayIconSlug = t.capturedAs?.iconSlug ?? t.iconSlug;
          const iconUrl = documentIconUrl(displayIconSlug);
          const isPreviewSource = previewTileId === t.tileId;
          return (
            <Fragment key={t.tileId}>
              <button
                type="button"
                role="listitem"
                data-tile-id={t.tileId}
                aria-expanded={isPreviewSource}
                className={`pdw-docs-tile pdw-docs-tile--${t.status}${!required && t.status === 'idle' ? ' pdw-docs-tile--optional' : ''}${isPreviewSource ? ' pdw-docs-tile--preview-open' : ''}${t.capturedAs ? ' pdw-docs-tile--adopted' : ''}`}
                onClick={() => onTileClick(t.tileId)}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f && onTileDrop) onTileDrop(t.tileId, f);
                }}
                aria-label={`${displayLabel} — ${tileStatusHint(t)}`}
                title={t.status === 'captured'
                  ? 'Click to open quick preview (view extracted fields, re-scan, or delete)'
                  : undefined}
              >
                <div className="pdw-docs-tile__head">
                  <div className="pdw-docs-tile__icon" aria-hidden>
                    {iconUrl ? (
                      <img src={iconUrl} alt="" width={40} height={40} />
                    ) : (
                      <span className="pdw-docs-tile__icon-fallback">{displayLabel.charAt(0)}</span>
                    )}
                  </div>
                  <div className="pdw-docs-tile__pill-slot">
                    {required ? (
                      <Pill variant="err">REQUIRED</Pill>
                    ) : (
                      <Pill variant="muted">OPTIONAL</Pill>
                    )}
                    {t.status === 'captured' && (
                      <span className="pdw-docs-tile__pill-stack">
                        <Pill variant="ok">✓ Captured</Pill>
                      </span>
                    )}
                    {/* Step 2 — slot label so the FSR sees "Primary ID" /
                        "Secondary ID" alongside the adopted doc-type label. */}
                    {t.capturedAs && (t.tileId === 'primary-id' || t.tileId === 'secondary-id') && (
                      <Pill variant="muted">
                        {t.tileId === 'primary-id' ? 'PRIMARY ID' : 'SECONDARY ID'}
                      </Pill>
                    )}
                  </div>
                </div>
                <div className="pdw-docs-tile__label">{displayLabel}</div>
                {/* Captured-tile filename strip (Bill 2026-05-13 PDW Step 8). */}
                {t.status === 'captured' && t.filename && (
                  <div className="pdw-docs-tile__filename" title={t.filename}>
                    <span className="pdw-docs-tile__filename-icon" aria-hidden>📄</span>
                    <span className="pdw-docs-tile__filename-text">{t.filename}</span>
                    {t.pageCount && t.pageCount > 1 && (
                      <span className="pdw-docs-tile__filename-pages">1/{t.pageCount}</span>
                    )}
                  </div>
                )}
                <div className="pdw-docs-tile__hint">{t.hint}</div>
                <div className="pdw-docs-tile__status">{tileStatusHint(t)}</div>
              </button>
              {isPreviewSource && previewTile && (
                <QuickPreviewDrawer
                  ref={drawerRef}
                  tile={previewTile}
                  onClose={onPreviewClose}
                  onRescan={onPreviewRescan}
                  {...(onPreviewReview && reviewableTileIds?.has(previewTile.tileId) ? { onReview: onPreviewReview } : {})}
                  onAddPage={onPreviewAddPage}
                  onDelete={onPreviewDelete}
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {(onReturn || onContinue) && (
        <footer className="pdw-docs-grid__footer">
          {onReturn ? (
            <Button
              variant="ghost"
              onClick={onReturn}
              title="Save and exit to previous step"
            >
              ← Back
            </Button>
          ) : <span />}
          {onContinue && (
            <Button
              variant="primary"
              onClick={onContinue}
              disabled={continueDisabled}
              title={continueTooltip}
            >
              {continueLabel}
            </Button>
          )}
        </footer>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// QuickPreviewDrawer — inline expand-in-place drawer (SPEC Screen 16, line
// 1440-1451). Per SPEC `Locked behaviors`, this is intentionally NOT a stacked
// modal: it renders as a sibling of the source tile inside the grid, spanning
// the full row width so the docs-grid context stays visible. Thumbnail uses
// `<img>` for image MIME and PdfCanvasViewer (pdf.js) for application/pdf.
//
// Acceptance criteria (BACKLOG-0149):
//   1. opens in-place                              — handled by parent grid
//   2. thumbnail + extracted fields + action row   — below
//   3. multi-page indicator                        — "Page 1 of N" pill
//   4. Esc + outside-click close                   — handled by parent grid
//   5. NOT editable (per SPEC line 1262)           — fields are <dl> read-only
//
// FSR cannot edit extracted fields here; edits happen in the Review form.
// -----------------------------------------------------------------------------

interface QuickPreviewDrawerProps {
  tile: DocTileSpec;
  onClose?: () => void;
  onRescan?: (tileId: string) => void;
  onReview?: (tileId: string) => void;
  onAddPage?: (tileId: string) => void;
  onDelete?: (tileId: string) => void;
}

const QuickPreviewDrawer = forwardRef<HTMLDivElement, QuickPreviewDrawerProps>(
  function QuickPreviewDrawer({ tile, onClose, onRescan, onReview, onAddPage, onDelete }, ref) {
    const fallbackIconUrl = documentIconUrl(tile.iconSlug);
    const isImage = tile.thumbnailMimeType?.startsWith('image/') ?? false;
    const isPdf = tile.thumbnailMimeType === 'application/pdf';

    return (
      <div
        ref={ref}
        className="pdw-docs-drawer"
        role="region"
        aria-label={`Quick preview — ${tile.label}`}
      >
        <div className="pdw-docs-drawer__head">
          <div className="pdw-docs-drawer__title">
            <strong>{tile.label}</strong>
            <span className="pdw-docs-drawer__meta">
              {tile.filename ?? 'Captured document'}
              {tile.pageCount && tile.pageCount > 1 && (
                <span className="pdw-docs-drawer__pages">
                  · Page 1 of {tile.pageCount}
                </span>
              )}
            </span>
          </div>
          {onClose && (
            <button
              type="button"
              className="pdw-docs-drawer__close"
              onClick={onClose}
              aria-label="Close quick preview"
              title="Close (Esc)"
            >
              ×
            </button>
          )}
        </div>

        <div className="pdw-docs-drawer__body">
          <div className="pdw-docs-drawer__thumb">
            {tile.thumbnailDataUrl && isImage && (
              <img
                src={tile.thumbnailDataUrl}
                alt={`${tile.label} — captured scan`}
                className="pdw-docs-drawer__thumb-img"
              />
            )}
            {tile.thumbnailDataUrl && isPdf && (
              // pdf.js, not an <iframe>: iframe PDFs are blank on bank PCs (CHG-0097).
              <PdfCanvasViewer
                source={tile.thumbnailDataUrl}
                title={`${tile.label} — captured scan`}
                className="pdw-docs-drawer__thumb-pdf"
              />
            )}
            {!tile.thumbnailDataUrl && (
              <div className="pdw-docs-drawer__thumb-placeholder" aria-hidden>
                {fallbackIconUrl ? (
                  <img src={fallbackIconUrl} alt="" width={96} height={96} />
                ) : (
                  <span>{tile.label.charAt(0)}</span>
                )}
                <span className="pdw-docs-drawer__thumb-placeholder-note">
                  Thumbnail unavailable
                </span>
              </div>
            )}
            {tile.pageCount && tile.pageCount > 1 && (
              <span className="pdw-docs-drawer__thumb-pagepill">1/{tile.pageCount}</span>
            )}
          </div>

          <div className="pdw-docs-drawer__fields">
            <div className="pdw-docs-drawer__fields-head">Extracted fields</div>
            {tile.extractedFields && tile.extractedFields.length > 0 ? (
              <dl className="pdw-docs-drawer__fields-list">
                {tile.extractedFields.map((row) => {
                  const conf = row.confidence;
                  // Bill 2026-06-03: a negative sentinel = manually entered /
                  // derived (no OCR score) → show "manual", never a fabricated %.
                  const confBand = conf == null
                    ? null
                    : conf < 0 ? 'manual'
                    : conf >= 0.9 ? 'hi' : conf >= 0.7 ? 'med' : 'lo';
                  return (
                    <div key={row.key} className="pdw-docs-drawer__fields-row">
                      <dt className="pdw-docs-drawer__fields-key">{row.key}</dt>
                      <dd className="pdw-docs-drawer__fields-val">
                        <span>{row.value}</span>
                        {confBand && (
                          <span
                            className={`pdw-docs-drawer__fields-conf pdw-docs-drawer__fields-conf--${confBand}`}
                            title={(conf as number) < 0
                              ? 'Manually entered / derived — not an OCR-scored field'
                              : `Azure DI confidence: ${Math.round((conf as number) * 100)}%`}
                          >
                            {(conf as number) < 0 ? 'manual' : `${Math.round((conf as number) * 100)}%`}
                          </span>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            ) : (
              <div className="pdw-docs-drawer__fields-empty">
                Extracted fields not available. Edits happen in the Review form.
              </div>
            )}
            <div className="pdw-docs-drawer__fields-note">
              Read-only preview — to edit values, open the Review form for this person.
            </div>
          </div>
        </div>

        <footer className="pdw-docs-drawer__foot">
          {onReview && (
            <button
              type="button"
              className="pdw-docs-drawer__btn pdw-docs-drawer__btn--ghost"
              onClick={() => onReview(tile.tileId)}
              title="Reopen the values read from this document and correct anything missed"
            >
              <span>Review import</span>
            </button>
          )}
          <button
            type="button"
            className="pdw-docs-drawer__btn pdw-docs-drawer__btn--ghost"
            onClick={() => onRescan?.(tile.tileId)}
            disabled={!onRescan}
            title={onRescan ? 'Replace this scan with a new capture' : 'Re-scan wiring pending'}
          >
            <img src="/icons/fi-rr-refresh.svg" alt="" width={14} height={14} />
            <span>Re-scan</span>
          </button>
          <button
            type="button"
            className="pdw-docs-drawer__btn pdw-docs-drawer__btn--ghost"
            onClick={() => onAddPage?.(tile.tileId)}
            disabled={!onAddPage}
            title={onAddPage ? 'Append an additional page to this document' : 'Add-page wiring pending'}
          >
            <img src="/icons/fi-rr-add-document.svg" alt="" width={14} height={14} />
            <span>Add page</span>
          </button>
          <button
            type="button"
            className="pdw-docs-drawer__btn pdw-docs-drawer__btn--danger"
            onClick={() => onDelete?.(tile.tileId)}
            disabled={!onDelete}
            title={onDelete ? 'Remove this captured scan (soft-delete, audit retained)' : 'Delete wiring pending'}
          >
            <img src="/icons/fi-rr-trash.svg" alt="" width={14} height={14} />
            <span>Delete</span>
          </button>
          <div className="pdw-docs-drawer__foot-spacer" />
          {onClose && (
            <button
              type="button"
              className="pdw-docs-drawer__btn pdw-docs-drawer__btn--primary"
              onClick={onClose}
            >
              Close
            </button>
          )}
        </footer>
      </div>
    );
  },
);
